/**
 * HTML tables for the Quill 1.3 editor used by react-quill 2.
 * Quill 1 has no table module, and quill-better-table targets Quill 2.
 * The cell, row, body, and table classes are one format, so they live together.
 */
/* eslint-disable max-classes-per-file, unicorn/prefer-dom-node-append -- Quill blots use appendChild, not DOM append */
import Quill from 'quill';

const Parchment = Quill.import('parchment');
const createBlot = Parchment.create as (name: string, value?: Partial<CellIds>) => TableBlot;
const Container = Quill.import('blots/container');
const Block = Quill.import('blots/block');
const QuillModule = Quill.import('core/module');
const Delta = Quill.import('delta');

const BLOCK_SCOPE = Parchment.Scope.BLOCK_BLOT;
const CELL_LINE = 'table-cell-line';

interface CellIds {
  table: string;
  row: string;
  cell: string;
}

interface TableBlot {
  domNode: HTMLElement;
  parent: TableBlot | null;
  prev: TableBlot | null;
  next: TableBlot | null;
  statics: { blotName: string };
  children: {
    length: number;
    map: <T>(callback: (child: TableBlot) => T) => T[];
  };
  length: () => number;
  remove: () => void;
  insertBefore: (child: TableBlot, ref?: TableBlot | null) => void;
  wrap: (name: string, value?: Partial<CellIds>) => void;
  appendChild: (child: TableBlot) => void;
  moveChildren: (target: TableBlot) => void;
}

interface DeltaOp {
  insert?: unknown;
  attributes?: Record<string, unknown>;
}

interface EditorDelta {
  ops?: DeltaOp[];
  retain: (length: number) => EditorDelta;
  insert: (text: string, attributes?: Record<string, unknown>) => EditorDelta;
  reduce: (
    callback: (next: EditorDelta, op: DeltaOp) => EditorDelta,
    initial: EditorDelta,
  ) => EditorDelta;
}

type QuillEditor = InstanceType<typeof Quill>;

function uid(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

function readIds(node: HTMLElement): Partial<CellIds> {
  return {
    table: node.dataset.table || undefined,
    row: node.dataset.row || undefined,
    cell: node.dataset.cell || undefined,
  };
}

function writeIds(node: HTMLElement, value?: Partial<CellIds>) {
  if (!value) return;

  const nextIds: Record<string, string> = {};

  if (value.table) nextIds.table = value.table;
  if (value.row) nextIds.row = value.row;
  if (value.cell) nextIds.cell = value.cell;

  Object.assign(node.dataset, nextIds);
}

function sameId(blot: TableBlot | null, key: 'table' | 'row' | 'cell', value: string | null) {
  if (!blot || !value) return false;

  return blot.domNode.dataset[key] === value;
}

function ensureAttr(node: HTMLElement | null, key: 'table' | 'row' | 'cell', prefix: string) {
  if (!node) return uid(prefix);

  const current = node.dataset[key];

  if (current) return current;

  const created = uid(prefix);

  Object.assign(node.dataset, { [key]: created });

  return created;
}

class TableCellLine extends Block {
  static create(value: CellIds) {
    const node = super.create() as HTMLElement;

    writeIds(node, value);

    return node;
  }

  static formats(domNode: HTMLElement): CellIds | undefined {
    const ids = readIds(domNode);

    if (!ids.table || !ids.row || !ids.cell) return undefined;

    return { table: ids.table, row: ids.row, cell: ids.cell };
  }

  format(name: string, value: CellIds | false | null) {
    if (name === CELL_LINE && value) {
      writeIds(this.domNode, value);

      return;
    }

    if (name === 'header' || name === 'list' || name === 'blockquote') return;

    super.format(name, value);
  }

  optimize(context: unknown) {
    const ids = readIds(this.domNode);
    const parent = this.parent as TableBlot | null;
    const previous = this.prev as TableBlot | null;

    if (parent?.statics.blotName !== 'table-cell') {
      // #region agent log
      fetch('http://127.0.0.1:7716/ingest/598963fa-6734-40a4-8fc6-bd197002a431',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6fa552'},body:JSON.stringify({sessionId:'6fa552',hypothesisId:'C',location:'table.ts:TableCellLine.optimize',message:'line parent is not a cell',data:{parent:parent?.statics.blotName||null,prev:previous?.statics.blotName||null,html:this.domNode.outerHTML},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      if (previous?.statics.blotName === 'table-cell' && sameId(previous, 'cell', ids.cell || null)) {
        previous.appendChild(this as unknown as TableBlot);
      } else {
        this.wrap('table-cell', ids);
      }
    }

    super.optimize(context);
  }
}

TableCellLine.blotName = CELL_LINE;
TableCellLine.tagName = 'P';
TableCellLine.className = 'ql-table-line';
TableCellLine.scope = BLOCK_SCOPE;

class TableCell extends Container {
  static create(value?: Partial<CellIds>) {
    const node = super.create() as HTMLElement;

    writeIds(node, value);

    return node;
  }

  optimize(context: unknown) {
    const ids = readIds(this.domNode);
    const parent = this.parent as TableBlot | null;
    const previous = this.prev as TableBlot | null;

    if (parent?.statics.blotName !== 'table-row') {
      if (previous?.statics.blotName === 'table-row' && sameId(previous, 'row', ids.row || null)) {
        previous.appendChild(this as unknown as TableBlot);
      } else {
        this.wrap('table-row', { table: ids.table, row: ids.row });
      }
    }

    super.optimize(context);
  }
}

TableCell.blotName = 'table-cell';
TableCell.tagName = 'TD';
TableCell.scope = BLOCK_SCOPE;

class TableRow extends Container {
  static create(value?: Partial<CellIds>) {
    const node = super.create() as HTMLElement;

    writeIds(node, value);

    return node;
  }

  optimize(context: unknown) {
    const ids = readIds(this.domNode);
    const parent = this.parent as TableBlot | null;
    const previous = this.prev as TableBlot | null;

    if (parent?.statics.blotName !== 'table-body') {
      if (previous?.statics.blotName === 'table-body' && sameId(previous, 'table', ids.table || null)) {
        previous.appendChild(this as unknown as TableBlot);
      } else {
        this.wrap('table-body', { table: ids.table });
      }
    }

    super.optimize(context);
  }
}

TableRow.blotName = 'table-row';
TableRow.tagName = 'TR';
TableRow.scope = BLOCK_SCOPE;

class TableBody extends Container {
  static create(value?: Partial<CellIds>) {
    const node = super.create() as HTMLElement;

    writeIds(node, value);

    return node;
  }

  optimize(context: unknown) {
    const ids = readIds(this.domNode);
    const parent = this.parent as TableBlot | null;
    const previous = this.prev as TableBlot | null;

    if (parent?.statics.blotName !== 'table-container') {
      if (previous?.statics.blotName === 'table-container' && sameId(previous, 'table', ids.table || null)) {
        previous.appendChild(this as unknown as TableBlot);
      } else {
        this.wrap('table-container', { table: ids.table });
      }
    }

    super.optimize(context);

    const next = this.next as TableBlot | null;

    if (next?.statics.blotName === 'table-body' && sameId(next, 'table', ids.table || null)) {
      next.moveChildren(this as unknown as TableBlot);
      next.remove();
    }
  }
}

TableBody.blotName = 'table-body';
TableBody.tagName = 'TBODY';
TableBody.scope = BLOCK_SCOPE;

class TableContainer extends Container {
  static create(value?: Partial<CellIds>) {
    const node = super.create() as HTMLElement;

    writeIds(node, value);
    node.classList.add('ql-article-table');

    return node;
  }

  optimize(context: unknown) {
    super.optimize(context);

    const tableId = this.domNode.dataset.table || null;
    const next = this.next as TableBlot | null;

    if (next?.statics.blotName === 'table-container' && sameId(next, 'table', tableId)) {
      next.moveChildren(this as unknown as TableBlot);
      next.remove();
    }
  }
}

TableContainer.blotName = 'table-container';
TableContainer.tagName = 'TABLE';
TableContainer.className = 'ql-article-table';
TableContainer.scope = BLOCK_SCOPE;

TableContainer.allowedChildren = [TableBody];
TableBody.requiredContainer = TableContainer;
TableBody.allowedChildren = [TableRow];
TableRow.requiredContainer = TableBody;
TableRow.allowedChildren = [TableCell];
TableCell.requiredContainer = TableRow;
TableCell.allowedChildren = [TableCellLine];
TableCellLine.requiredContainer = TableCell;

function cellIdsFromNode(node: HTMLElement): CellIds {
  const rowNode = node.closest('tr');
  const tableNode = node.closest('table');

  return {
    table: ensureAttr(tableNode, 'table', 'table'),
    row: ensureAttr(rowNode, 'row', 'row'),
    cell: ensureAttr(node, 'cell', 'cell'),
  };
}

function applyCellFormat(delta: EditorDelta, ids: CellIds) {
  return delta.reduce((next, op) => {
    if (typeof op.insert !== 'string') {
      return next.insert(String(op.insert), op.attributes);
    }

    const attributes = { ...(op.attributes || {}) };

    delete attributes.header;
    delete attributes.list;
    delete attributes.blockquote;
    delete attributes[CELL_LINE];

    op.insert.split('\n').forEach((piece, index, pieces) => {
      if (piece.length > 0) next.insert(piece, attributes);

      if (index < pieces.length - 1) {
        next.insert('\n', { ...attributes, [CELL_LINE]: ids });
      }
    });

    return next;
  }, new Delta());
}

function matchCell(node: HTMLElement, delta: EditorDelta) {
  const ids = cellIdsFromNode(node);
  const formatted = applyCellFormat(delta, ids);
  const last = formatted.ops?.[formatted.ops.length - 1];
  const hasLine = typeof last?.insert === 'string' && last.insert.endsWith('\n');

  if (!hasLine) formatted.insert('\n', { [CELL_LINE]: ids });

  return formatted;
}

function childList(parent: TableBlot) {
  return parent.children.map((child) => child);
}

function createCell(ids: CellIds) {
  const cell = createBlot('table-cell', ids);
  const line = createBlot(CELL_LINE, ids);

  // A new row is inserted as one node, so Quill never optimizes the lines inside it.
  // An empty line has no height, and typing then inserts text into the cell.
  line.appendChild(createBlot('break'));
  cell.appendChild(line);

  return cell;
}

function describeCells(row: TableBlot) {
  return childList(row).map((cell) => ({
    html: cell.domNode.innerHTML,
    cellHeight: cell.domNode.offsetHeight,
    lines: childList(cell).map((line) => ({
      name: line.statics.blotName,
      html: line.domNode.innerHTML,
      height: line.domNode.offsetHeight,
      children: line.children.length,
    })),
  }));
}

function lineIds(line: TableBlot | null): CellIds | null {
  if (!line || line.statics.blotName !== CELL_LINE) return null;

  const ids = readIds(line.domNode);

  if (!ids.table || !ids.row || !ids.cell) return null;

  return { table: ids.table, row: ids.row, cell: ids.cell };
}

function blotParent(blot: TableBlot | null, blotName: string) {
  let current = blot;

  while (current && current.statics.blotName !== blotName) {
    current = current.parent;
  }

  return current;
}

class ArticleTable extends QuillModule {
  menu: HTMLDivElement | null = null;

  controls: HTMLDivElement | null = null;

  activeCell: TableBlot | null = null;

  activeRow: TableBlot | null = null;

  activeTable: TableBlot | null = null;

  constructor(quill: QuillEditor, options: Record<string, unknown>) {
    super(quill, options);
    this.quill.clipboard.addMatcher('td', matchCell);
    this.quill.clipboard.addMatcher('th', matchCell);
    this.quill.root.addEventListener('contextmenu', (event: MouseEvent) => this.onContextMenu(event));
    this.quill.root.addEventListener('scroll', () => this.positionControls());
    this.quill.on('selection-change', (range: { index: number } | null) => this.syncControls(range));
    this.quill.root.addEventListener('keydown', (event: KeyboardEvent) => {
      const range = this.quill.getSelection();
      const [line] = range ? this.quill.getLine(range.index) as [TableBlot | null, number] : [null, 0];
      const parent = line?.parent as TableBlot | null;
      // #region agent log
      fetch('http://127.0.0.1:7716/ingest/598963fa-6734-40a4-8fc6-bd197002a431',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6fa552'},body:JSON.stringify({sessionId:'6fa552',hypothesisId:'D',location:'table.ts:keydown',message:'caret target before key',data:{key:event.key,index:range?.index??null,line:line?.statics.blotName||null,parent:parent?.statics.blotName||null,lineHtml:line?.domNode.innerHTML??null,lineHeight:line?.domNode.offsetHeight??null},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
    }, true);
    window.addEventListener('error', (event) => {
      if (!String(event.message || '').toLowerCase().includes('table')) return;
      // #region agent log
      fetch('http://127.0.0.1:7716/ingest/598963fa-6734-40a4-8fc6-bd197002a431',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6fa552'},body:JSON.stringify({sessionId:'6fa552',hypothesisId:'D',location:'table.ts:window.error',message:'table insert error',data:{message:event.message},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
    });
  }

  insertTable(rows = 2, columns = 3) {
    const range = this.quill.getSelection(true);

    if (!range) return;

    const [currentLine] = this.quill.getLine(range.index) as [TableBlot | null, number];
    let { index } = range;
    const insideTable = lineIds(currentLine);

    if (insideTable && currentLine) {
      const table = blotParent(currentLine, 'table-container');

      if (!table) return;

      index = this.quill.getIndex(table) + table.length();
    }

    const tableId = uid('table');
    const cells = Array.from({ length: rows }, () => {
      const rowId = uid('row');

      return Array.from({ length: columns }, () => ({
        table: tableId,
        row: rowId,
        cell: uid('cell'),
      }));
    }).flat();

    let delta = new Delta().retain(index).insert('\n') as EditorDelta;

    cells.forEach((ids) => {
      delta = delta.insert('\n', { [CELL_LINE]: ids });
    });

    this.quill.updateContents(delta, Quill.sources.USER);
    this.quill.setSelection(index + 1, 0, Quill.sources.SILENT);
  }

  insertRow(row: TableBlot, placement: 'above' | 'below') {
    const body = row.parent;

    if (!body || !readIds(row.domNode).table) return;

    const tableId = readIds(row.domNode).table as string;
    const rowId = uid('row');
    const newRow = createBlot('table-row', { table: tableId, row: rowId });

    row.children.map(() => uid('cell')).forEach((cellId) => {
      newRow.appendChild(createCell({ table: tableId, row: rowId, cell: cellId }));
    });

    body.insertBefore(newRow, placement === 'above' ? row : row.next);
    this.quill.update(Quill.sources.USER);
    // #region agent log
    fetch('http://127.0.0.1:7716/ingest/598963fa-6734-40a4-8fc6-bd197002a431',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6fa552'},body:JSON.stringify({sessionId:'6fa552',hypothesisId:'A',location:'table.ts:insertRow',message:'new row after update',data:describeCells(newRow),timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    this.finishStructureEdit();
  }

  insertColumn(cell: TableBlot, placement: 'left' | 'right') {
    const row = cell.parent;
    const body = row?.parent;

    if (!row || !body) return;

    const columnIndex = childList(row).indexOf(cell) + (placement === 'right' ? 1 : 0);

    childList(body).forEach((currentRow) => {
      const rowIds = readIds(currentRow.domNode);

      if (!rowIds.table || !rowIds.row) return;

      const newCell = createCell({ table: rowIds.table, row: rowIds.row, cell: uid('cell') });

      currentRow.insertBefore(newCell, childList(currentRow)[columnIndex] || null);
    });

    this.quill.update(Quill.sources.USER);
    // #region agent log
    fetch('http://127.0.0.1:7716/ingest/598963fa-6734-40a4-8fc6-bd197002a431',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6fa552'},body:JSON.stringify({sessionId:'6fa552',hypothesisId:'B',location:'table.ts:insertColumn',message:'column cells after update',data:{rows:childList(body).map((item)=>describeCells(item))},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    this.finishStructureEdit();
  }

  deleteRow(row: TableBlot) {
    const body = row.parent;
    const table = body?.parent;

    row.remove();

    if (body && body.children.length === 0) table?.remove();

    this.quill.update(Quill.sources.USER);
    this.finishStructureEdit();
  }

  deleteColumn(cell: TableBlot) {
    const row = cell.parent;
    const body = row?.parent;

    if (!row || !body) return;

    const columnIndex = childList(row).indexOf(cell);

    childList(body).forEach((currentRow) => {
      childList(currentRow)[columnIndex]?.remove();
    });

    if (row.children.length === 0) body.parent?.remove();

    this.quill.update(Quill.sources.USER);
    this.finishStructureEdit();
  }

  deleteTable(table: TableBlot) {
    table.remove();
    this.quill.update(Quill.sources.USER);
    this.finishStructureEdit();
  }

  finishStructureEdit() {
    const cellConnected = this.activeCell?.domNode.isConnected;
    const tableConnected = this.activeTable?.domNode.isConnected;

    if (!cellConnected || !tableConnected) {
      this.closeControls();

      return;
    }

    this.positionControls();
  }

  syncControls(range: { index: number } | null) {
    if (!range || !this.quill.isEnabled()) {
      this.closeControls();

      return;
    }

    const [line] = this.quill.getLine(range.index) as [TableBlot | null, number];
    const cell = blotParent(line, 'table-cell');
    const row = blotParent(line, 'table-row');
    const table = blotParent(line, 'table-container');

    if (!cell || !row || !table) {
      this.closeControls();

      return;
    }

    this.activeCell = cell;
    this.activeRow = row;
    this.activeTable = table;
    this.showControls();
  }

  showControls() {
    if (!this.controls) {
      const controls = document.createElement('div');

      controls.className = 'article-table-controls';

      const actions: [string, () => void][] = [
        ['Add column', () => this.activeCell && this.insertColumn(this.activeCell, 'right')],
        ['Add row', () => this.activeRow && this.insertRow(this.activeRow, 'below')],
        ['Delete column', () => this.activeCell && this.deleteColumn(this.activeCell)],
        ['Delete row', () => this.activeRow && this.deleteRow(this.activeRow)],
        ['Delete table', () => this.activeTable && this.deleteTable(this.activeTable)],
      ];

      actions.forEach(([label, action]) => {
        const button = document.createElement('button');

        button.type = 'button';
        button.textContent = label;
        button.addEventListener('mousedown', (mouseEvent) => mouseEvent.preventDefault());
        button.addEventListener('click', () => action());
        controls.append(button);
      });

      document.body.append(controls);
      this.controls = controls;
    }

    this.positionControls();
  }

  positionControls() {
    if (!this.controls || !this.activeTable?.domNode.isConnected) return;

    const rect = this.activeTable.domNode.getBoundingClientRect();

    this.controls.style.left = `${rect.left}px`;
    this.controls.style.top = `${Math.max(8, rect.top - 36)}px`;
  }

  closeControls() {
    this.controls?.remove();
    this.controls = null;
    this.activeCell = null;
    this.activeRow = null;
    this.activeTable = null;
  }

  onContextMenu(event: MouseEvent) {
    if (!this.quill.isEnabled()) return;

    const target = event.target as HTMLElement | null;
    const cellNode = target?.closest('td');

    if (!cellNode) return;

    const cell = Quill.find(cellNode) as TableBlot | null;
    const row = cell?.parent;
    const table = blotParent(cell, 'table-container');

    if (!cell || !row || !table || cell.statics.blotName !== 'table-cell') return;

    event.preventDefault();
    this.closeMenu();

    const menu = document.createElement('div');

    menu.className = 'article-table-menu';
    menu.style.left = `${event.clientX}px`;
    menu.style.top = `${event.clientY}px`;

    const actions: [string, () => void][] = [
      ['Insert row above', () => this.insertRow(row, 'above')],
      ['Insert row below', () => this.insertRow(row, 'below')],
      ['Insert column left', () => this.insertColumn(cell, 'left')],
      ['Insert column right', () => this.insertColumn(cell, 'right')],
      ['Delete row', () => this.deleteRow(row)],
      ['Delete column', () => this.deleteColumn(cell)],
      ['Delete table', () => this.deleteTable(table)],
    ];

    actions.forEach(([label, action]) => {
      const button = document.createElement('button');

      button.type = 'button';
      button.textContent = label;
      button.addEventListener('mousedown', (mouseEvent) => mouseEvent.preventDefault());
      button.addEventListener('click', () => {
        action();
        this.closeMenu();
      });
      menu.append(button);
    });

    document.body.append(menu);
    this.menu = menu;

    const close = (mouseEvent: MouseEvent) => {
      if (mouseEvent.target instanceof Node && menu.contains(mouseEvent.target)) return;

      this.closeMenu();
    };

    window.setTimeout(() => {
      document.addEventListener('mousedown', close, { once: true });
    }, 0);
  }

  closeMenu() {
    this.menu?.remove();
    this.menu = null;
  }
}

Quill.register({
  [`formats/${CELL_LINE}`]: TableCellLine,
  'formats/table-cell': TableCell,
  'formats/table-row': TableRow,
  'formats/table-body': TableBody,
  'formats/table-container': TableContainer,
  'modules/articleTable': ArticleTable,
}, true);
